package com.dotdrives.controller

import com.dotdrives.model.FormPaymentReadiness
import com.dotdrives.core.DotController
import com.dotdrives.model.FormOrgStats
import com.dotdrives.model.FormStats
import com.dotdrives.model.GivingForm
import com.dotdrives.model.Target
import com.dotdrives.repository.FormRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

data class FormsBoardUiState(
    val loading: Boolean = false,
    val forms: List<GivingForm> = emptyList(),
    /**
     * null = zero `form_submissions` rows were visible for this org's forms at all -- render
     * every card's money/gift numbers as "-", not "0" (docs/schema.md: form_submissions is
     * has_perm_strict-gated and an RLS-empty result is a legitimate state, not an error).
     */
    val stats: Map<String, FormStats>? = null,
    val orgStats: FormOrgStats? = null,
    /** Distinct active recurring givers; same ambiguous-null contract as [stats]. */
    val activePartnersCount: Int? = null,
    val stripeConnected: Boolean? = null,
    val paymentReadiness: FormPaymentReadiness? = null,
    val targets: List<Target> = emptyList(),
    val error: String? = null,
)

class FormsBoardController(
    private val repo: FormRepository,
    scope: CoroutineScope,
    private val orgId: String,
) : DotController<FormsBoardUiState>(FormsBoardUiState(), scope) {
    private var generation = 0L

    fun load() {
        val gen = nextGeneration()
        update { it.copy(loading = true, error = null) }
        scope.launch {
            try {
                val snapshot = repo.dashboard(orgId).getOrThrow()
                if (!isCurrent(gen)) return@launch
                update {
                    it.copy(
                        loading = false,
                        forms = snapshot.forms,
                        stats = snapshot.stats,
                        orgStats = snapshot.orgStats,
                        activePartnersCount = snapshot.activePartnersCount,
                        stripeConnected = snapshot.stripeConnected,
                        paymentReadiness = snapshot.paymentReadiness,
                        targets = snapshot.targets,
                        error = null,
                    )
                }
            } catch (error: Exception) {
                publishError(error, gen)
            }
        }
    }

    fun retry() = load()

    private fun publishError(error: Throwable, gen: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(gen)) return
        update {
            if (isCurrent(gen)) it.copy(loading = false, error = error.message ?: "Something went wrong") else it
        }
    }

    private fun nextGeneration(): Long {
        generation += 1
        return generation
    }

    private fun isCurrent(gen: Long): Boolean = gen == generation
}
